import type { ReactNode } from "react";

// Ícone por card, casado pelo slug de `public.cards`.
//
// Por que um mapa por slug e não uma coluna `icone` no banco: o desenho do ícone
// é apresentação, não catálogo — mudar de biblioteca ou de traço não deveria
// virar migration. O que o banco guarda (nome, descrição, url, ordem) é o que a
// operação edita; o traço é do front.
//
// Slug sem entrada aqui cai no genérico, e a tela não quebra: card novo aparece
// com ícone neutro até alguém escolher um. Ícones no estilo Lucide (traço 2,
// cantos e pontas arredondadas), que é o do design system.

const T = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const ICONES: Record<string, ReactNode> = {
  // --- os quatro universais ---
  aulas: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M10 8.5 16 12l-6 3.5z" fill="currentColor" stroke="none" />
    </>
  ),
  comunidade: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9.5" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  "area-do-candidato": (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="2" />
      <path d="M15 9h3M15 13h3M6 17h12" />
    </>
  ),
  "area-do-contratante": (
    <>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </>
  ),

  // --- módulos do hub ---
  "secretaria-do-aluno": (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M9 15h6M9 11h3" />
    </>
  ),
  "minhas-notas": (
    <>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      <path d="M10 7h6M10 11h4" />
    </>
  ),

  // --- ferramentas ---
  "cfc-de-a-a-z": (
    <>
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
      <path d="M6 12v5c3 2.5 9 2.5 12 0v-5" />
    </>
  ),
  "contabiliza-ai": (
    <>
      <path d="M12 3l1.9 5.6L19.5 10l-5.6 1.4L12 17l-1.9-5.6L4.5 10l5.6-1.4z" />
      <path d="M18.5 15.5l.6 1.9 1.9.6-1.9.6-.6 1.9-.6-1.9-1.9-.6 1.9-.6z" />
    </>
  ),
  "mei-pro": (
    <>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      <path d="M2 13h20" />
    </>
  ),
  "simulador-mei": (
    <>
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M8 6h8M8 10.5h2.5M13.5 10.5H16M8 14.5h2.5M13.5 14.5H16M8 18.5h8" />
    </>
  ),
  "ir-pro": (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M9 13h6M9 17h4" />
    </>
  ),
  "ir-proposta": (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M9 15.5l1.8 1.8L14.5 13" />
    </>
  ),
  "gerador-de-curriculos": (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 21v-1a7.5 7.5 0 0 1 15 0v1" />
    </>
  ),
  "calendario-tributario": (
    <>
      <rect x="3" y="4.5" width="18" height="17" rx="2" />
      <path d="M16 2.5v4M8 2.5v4M3 10h18" />
      <path d="M8 14h2M14 14h2M8 18h2" />
    </>
  ),
  "hub-dp": (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M6 15.5h5M6 11.5h12M6 8h8" />
    </>
  ),
  "legaliza-facil": (
    <>
      <path d="M12 2.5v19M3.5 8 12 3.5 20.5 8" />
      <path d="M5.5 10.5V17a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2v-6.5" />
    </>
  ),
  "gerenciador-de-estudos": (
    <>
      <path d="M9 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4" />
      <path d="m14.5 3.5 6 6-7 7H10v-4.5z" />
    </>
  ),
  "tec-concursos": (
    <>
      <path d="M8 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M9 12.5l1.8 1.8L14.5 10" />
    </>
  ),
  "mapa-de-concursos": (
    <>
      <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0z" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
  "peric-ia": (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
      <path d="M11 8v6M8 11h6" />
    </>
  ),
  "leitor-de-processos": (
    <>
      <path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H18a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6.5A2.5 2.5 0 0 1 4 19.5z" />
      <path d="M8 7h8M8 11h8M8 15h5" />
    </>
  ),
  // A Central do Perito (000385): uma pasta de processo com as linhas da ficha.
  "central-do-perito": (
    <>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M8 13h8M8 16.5h5" />
    </>
  ),
  "foro-facil": (
    <>
      <path d="M12 3v18M7 21h10" />
      <path d="M4.5 8h15M6.5 8l-2.5 5h5zM17.5 8l-2.5 5h5z" />
    </>
  ),
  calculadoras: (
    <>
      <rect x="4" y="2" width="16" height="20" rx="2" />
      <path d="M8 6h8M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15v3.5M8 18.5h4.5" />
    </>
  ),
  "central-ibs-cbs": (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.6 2.5 15.4 0 18-2.5-2.6-2.5-15.4 0-18z" />
    </>
  ),
  "cst-e-classtrib": (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18M9 9v11M3 14.5h18" />
    </>
  ),
  sindifacil: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9.5" cy="7" r="4" />
      <path d="M19 8v6M16 11h6" />
    </>
  ),
  cnaexpert: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
      <path d="M8.5 11h5M11 8.5v5" opacity="0.55" />
    </>
  ),
  "radar-simples": (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4.5" />
      <path d="M12 12l6-6" />
    </>
  ),

  // --- Pós Auditoria e Compliance (000192): as duas ainda vão ser construídas.
  // O card já aparece como `em-breve`, e ícone genérico ao lado de vizinhos que
  // têm traço próprio se lê como card quebrado, não como card futuro.
  voucher: (
    <>
      <path d="M3 8.5V6a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v2.5a2.5 2.5 0 0 0 0 5V16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-2.5a2.5 2.5 0 0 0 0-5z" />
      <path d="M12 8v1.5M12 11.5v1M12 14.5V16" opacity="0.55" />
    </>
  ),
  sentinela: (
    <>
      <path d="M12 3l7 3v5.5c0 4.2-2.9 7.6-7 8.5-4.1-.9-7-4.3-7-8.5V6z" />
      <path d="M9.5 12l1.8 1.8L15 10" />
    </>
  ),

  // --- cursos bônus ---
  "ia-financas": (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M9 9h6v6H9z" />
      <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
    </>
  ),
  "excel-financas": (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 3v18" />
      <path d="M13 13l4 4M17 13l-4 4" />
    </>
  ),
  "sheets-financas": (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
    </>
  ),
};

const GENERICO = (
  <>
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <path d="M8 12h8M12 8v8" />
  </>
);

export function IconeDoCard({ slug, className = "size-[23px]" }: { slug: string; className?: string }) {
  // `width`/`height` em `1em` são a dimensão INTRÍNSECA, e não decoração: o
  // padrão de `className` é SUBSTITUÍDO por quem chama, então basta alguém passar
  // `className="shrink-0 text-cinza"` para o `size-[23px]` desaparecer — e SVG
  // com viewBox e sem dimensão estica até preencher o pai. Foi a cicatriz das 34
  // setas gigantes na primeira tela do IR Pro, consertada em `SetaDireita` e em
  // `IconeDeAcao`; este ícone tinha ficado de fora. Qualquer `size-*` continua
  // sobrepondo, então a armadilha deixa de existir sem mudar nenhuma chamada.
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" className={className} aria-hidden="true" {...T}>
      {ICONES[slug] ?? GENERICO}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Ícones de AÇÃO das telas de admin
// ---------------------------------------------------------------------------
// Victor, 20/08: "nas telas de admin, procure usar ícone nos que der pra usar" —
// as fileiras de pílulas com verbo por extenso pesavam a linha. A PALAVRA não
// sumiu: ela foi para o `title` e o `aria-label` de cada botão, então quem paira
// o mouse ou usa leitor de tela recebe o verbo inteiro. Texto continua onde
// carrega o que ícone não carrega: contagens ("Arquivar 3 selecionadas") e as
// confirmações de excluir.
//
// Dimensão INTRÍNSECA (width/height 1em) de propósito: padrão de className é
// SUBSTITUÍDO por quem chama, e SVG com viewBox e sem dimensão estica até o pai
// — a cicatriz das 34 setas gigantes.

function IconeDeAcao({ children, className = "size-[18px]" }: { children: ReactNode; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" className={className} aria-hidden="true" {...T}>
      {children}
    </svg>
  );
}

/** Lápis — renomear, editar. */
export function IconeEditar({ className }: { className?: string }) {
  return (
    <IconeDeAcao className={className}>
      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z" />
    </IconeDeAcao>
  );
}

/** Pasta aberta — entrar na área e ver as páginas dela. */
export function IconeAbrirPasta({ className }: { className?: string }) {
  return (
    <IconeDeAcao className={className}>
      <path d="M6 14 3.5 9.5A1 1 0 0 1 4.4 8H19a2 2 0 0 1 1.9 2.6L19.5 16a2 2 0 0 1-1.9 1.4H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4l2 3h7" />
    </IconeDeAcao>
  );
}

/** Olho — está visível para o aluno. */
export function IconeOlho({ className }: { className?: string }) {
  return (
    <IconeDeAcao className={className}>
      <path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12z" />
      <circle cx="12" cy="12" r="2.6" />
    </IconeDeAcao>
  );
}

/** Olho riscado — escondida do aluno. */
export function IconeOlhoRiscado({ className }: { className?: string }) {
  return (
    <IconeDeAcao className={className}>
      <path d="M10.6 6.1A10.8 10.8 0 0 1 12 5.5c6.5 0 10 6.5 10 6.5a17 17 0 0 1-2.4 3.2M6.6 7.3A16 16 0 0 0 2 12s3.5 6.5 10 6.5a10 10 0 0 0 3.4-.6" />
      <path d="M9.9 10.3a2.6 2.6 0 0 0 3.7 3.6" />
      <path d="m3 3 18 18" />
    </IconeDeAcao>
  );
}

/** Lixeira — excluir (o primeiro dos dois toques; a confirmação continua em texto). */
export function IconeLixeira({ className }: { className?: string }) {
  return (
    <IconeDeAcao className={className}>
      <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </IconeDeAcao>
  );
}

/** Setas em círculo — reprocessar. */
export function IconeReprocessar({ className }: { className?: string }) {
  return (
    <IconeDeAcao className={className}>
      <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
    </IconeDeAcao>
  );
}

/** Caixa de arquivo — arquivar. */
export function IconeArquivar({ className }: { className?: string }) {
  return (
    <IconeDeAcao className={className}>
      <path d="M3 4h18v4H3zM5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
      <path d="M10 12h4" />
    </IconeDeAcao>
  );
}

/** Seta saindo da caixa — devolver a pendência à fila. */
export function IconeDevolver({ className }: { className?: string }) {
  return (
    <IconeDeAcao className={className}>
      <path d="M3 4h18v4H3zM5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" />
      <path d="M12 17v-5M9.5 14.5 12 12l2.5 2.5" />
    </IconeDeAcao>
  );
}

/** Documento — abrir o dossiê do aluno. */
export function IconeDossie({ className }: { className?: string }) {
  return (
    <IconeDeAcao className={className}>
      <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
      <path d="M14 2v5h5M9 13h6M9 17h4" />
    </IconeDeAcao>
  );
}

/** X — fechar o que está aberto. */
export function IconeFechar({ className }: { className?: string }) {
  return (
    <IconeDeAcao className={className}>
      <path d="m5 5 14 14M19 5 5 19" />
    </IconeDeAcao>
  );
}

/** Divisa para baixo — gira 180° para "recolher". */
export function IconeDivisa({ className }: { className?: string }) {
  return (
    <IconeDeAcao className={className}>
      <path d="m5 9 7 7 7-7" />
    </IconeDeAcao>
  );
}
